import { useCallback, useEffect, useState } from 'react';
import { syncUrlToView, viewKeyFromPath } from '../lib/routes.js';

/**
 * Ticket #39 — écran actif adossé à l'URL, sans router.
 *
 * Remplace le `useState('home')` des shells desktop et mobile : l'état initial
 * est lu dans le pathname (donc un CTRL+R rouvre le même écran), chaque
 * navigation pousse une entrée d'historique (donc Précédent/Suivant du
 * navigateur fonctionnent), et `popstate` remet l'état en phase.
 *
 * @param {Set<string>|Map<string, unknown>} knownKeys clés de vue montables par ce shell
 * @param {string} fallbackKey vue de repli si l'URL ne désigne rien de connu
 * @returns {{ activeView: string, navigate: (key: string, opts?: {replace?: boolean}) => void }}
 */
export function useViewRoute(knownKeys, fallbackKey = 'home') {
  const [activeView, setActiveView] = useState(
    () => viewKeyFromPath(window.location.pathname, knownKeys) ?? fallbackKey,
  );

  // URL exotique ou vue inconnue de ce shell (ex. lien vers un écran desktop
  // ouvert sur mobile) : on réécrit l'URL sur la vue réellement affichée, en
  // `replace` pour ne pas créer une entrée d'historique fantôme.
  useEffect(() => {
    syncUrlToView(activeView, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setActiveView(viewKeyFromPath(window.location.pathname, knownKeys) ?? fallbackKey);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [knownKeys, fallbackKey]);

  const navigate = useCallback((key, { replace = false } = {}) => {
    setActiveView(key);
    syncUrlToView(key, { replace });
  }, []);

  return { activeView, navigate };
}
